import policyDocument from './telemetryPolicy.generated.json'

export type TelemetrySink = 'cloud_logging' | 'error_reporting' | 'log_metric'
export type SafeEventFields = Record<string, string | number | boolean>

interface FieldPolicy {
  classification: string
  name: string
  sinks: string[]
  transformation: string
}

const MAX_STRING_LENGTH = 512
const MAX_NUMBER = 1_000_000_000_000_000
const fieldPolicy = new Map(
  (policyDocument.fields as FieldPolicy[]).map((field) => [field.name, field]),
)
const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const secretAssignmentPattern =
  /\b(?:api[_-]?key|authorization|cookie|credential|password|secret|signature|token)\s*[=:]\s*[^\s,;]+/gi
const authValuePattern = /\b(?:basic|bearer)\s+[A-Z0-9._~+/-]+=*/gi
const urlQueryPattern = /(https?:\/\/[^\s?]+)\?[^\s]+/gi
const snakeCasePattern = /^[a-z][a-z0-9_]{0,127}$/
const boundedEnumPattern = /^[A-Za-z][A-Za-z0-9_.-]{0,127}$/
const classNamePattern = /^[A-Za-z_][A-Za-z0-9_.]{0,127}$/
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const traceIdPattern = /^(?!0{32}$)[0-9a-f]{32}$/
const spanIdPattern = /^(?!0{16}$)[0-9a-f]{16}$/
const gitShaPattern = /^[0-9a-f]{7,40}$/
const stringValidators: Record<string, (value: string) => boolean> = {
  registered_snake_case: (value) => snakeCasePattern.test(value),
  registered_enum: (value) => boundedEnumPattern.test(value),
  allowlisted_class_name: (value) => classNamePattern.test(value),
  validate_uuid: (value) => uuidPattern.test(value),
  validate_or_replace_uuid: (value) => uuidPattern.test(value),
  validate_w3c_trace_id: (value) => traceIdPattern.test(value),
  validate_w3c_span_id: (value) => spanIdPattern.test(value),
  server_owned_git_sha: (value) => value === 'local' || gitShaPattern.test(value),
  framework_template_without_parameters: (value) =>
    value.startsWith('/') && !value.includes('?') && !value.includes('{'),
}
const stringTransformations = new Set([
  'allowlisted_class_name',
  'framework_template_without_parameters',
  'normalize_rfc3339',
  'redact_and_truncate',
  'registered_enum',
  'registered_error_code',
  'registered_snake_case',
  'sanitized_frames_without_locals',
  'server_owned_enum',
  'server_owned_git_sha',
  'server_owned_rfc3339',
  'validate_or_replace_uuid',
  'validate_uuid',
  'validate_w3c_span_id',
  'validate_w3c_trace_id',
])
const integerTransformations = new Set([
  'bounded_integer',
  'bounded_nonnegative_integer',
  'require_integer_one',
])

function redactSensitiveText(value: string): string {
  return value
    .replace(emailPattern, '[REDACTED]')
    .replace(secretAssignmentPattern, '[REDACTED]')
    .replace(authValuePattern, '[REDACTED]')
    .replace(urlQueryPattern, '$1?[REDACTED]')
    .slice(0, MAX_STRING_LENGTH)
}

function sanitizeString(value: string, transformation: string): string | null {
  if (
    transformation === 'redact_and_truncate' ||
    transformation === 'sanitized_frames_without_locals'
  ) {
    return redactSensitiveText(value)
  }
  if (value.length > MAX_STRING_LENGTH) return null
  const validator = stringValidators[transformation]
  return !validator || validator(value) ? value : null
}

function sanitizeNumber(value: number, transformation: string): number | null {
  if (!Number.isFinite(value) || Math.abs(value) > MAX_NUMBER) return null
  if (transformation === 'require_integer_one') {
    return Number.isInteger(value) && value === 1 ? value : null
  }
  if (transformation.startsWith('bounded_nonnegative') && value < 0) return null
  if (transformation.endsWith('integer') && !Number.isInteger(value)) return null
  return value
}

function sanitizeValue(value: unknown, transformation: string): string | number | boolean | null {
  if (typeof value === 'string') {
    return stringTransformations.has(transformation) ? sanitizeString(value, transformation) : null
  }
  if (typeof value === 'number') {
    const acceptsNumber =
      integerTransformations.has(transformation) || transformation === 'bounded_nonnegative_number'
    return acceptsNumber ? sanitizeNumber(value, transformation) : null
  }
  if (typeof value === 'boolean') return transformation === 'require_boolean' ? value : null
  return null
}

export function sanitizeEvent(input: unknown, sink: TelemetrySink): SafeEventFields | null {
  try {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) return null
    const entries = Object.entries(input)
    if (entries.length === 0) return null

    const sanitized: SafeEventFields = {}
    for (const [name, value] of entries) {
      const rule = fieldPolicy.get(name)
      if (!rule || !rule.sinks.includes(sink) || rule.classification === 'prohibited') return null
      const safeValue = sanitizeValue(value, rule.transformation)
      if (safeValue === null) return null
      sanitized[name] = safeValue
    }
    return sanitized
  } catch {
    return null
  }
}
